import { defineStore } from 'pinia'
import { type DefaultObject } from '@/types'
import { useClientStore } from '@/stores/client'
import type { UpdateDataType } from '@/components/partials/display/VersionUpdate.vue'
import * as Sentry from '@sentry/vue'

export interface State {
  office: DefaultObject | null
  officeMeta: DefaultObject | null
  availableUpdates: UpdateDataType[] | null
}

export const useOfficeStore = defineStore('office', {
  state: (): State => ({
    office: null,
    officeMeta: null,
    availableUpdates: null,
  }),
  getters: {
    getOffice: (state): DefaultObject | null => state.office,
    getMeta: (state): DefaultObject | null => state.officeMeta,
    getVersion: (state) => state.office?.version,
    getAvailableUpdates: (state) => state.availableUpdates,
  },
  actions: {
    async initOffice(data: { architect_office?: DefaultObject; meta?: DefaultObject }) {
      this.$patch({
        office: data?.architect_office ?? null,
        officeMeta: data?.meta ?? null,
      })
      useClientStore().registerMetaUserId(typeof data?.meta?.userId === 'string' ? data.meta.userId : null)
      Sentry.setContext('office', data?.architect_office ?? null)
      Sentry.setContext('office_meta', data?.meta ?? null)
    },
    async updateOffice(data: Record<string, unknown>) {
      const office = this.getOffice
      if (office) {
        office.data = data
        office.updated = Date.now()
        this.$patch({ office: office })
      }
      return true
    },

    triggerClientSave() {
      const officeData = this.getOffice
      const user = useClientStore().getUser
      if (user && user.origin) {
        window.parent.postMessage({ type: 'on_save', data: JSON.stringify(officeData) }, user.origin)
      }
    },

    setAvailableUpdates(updates: UpdateDataType[]) {
      /* only one update expected ! */
      if (this.getMeta?.canEdit) {
        this.$patch({ availableUpdates: updates })
      }
    },

    versionUpdate(version: string) {
      const office = this.getOffice
      if (office) {
        office.version = version
        office.updated = Date.now()
        this.$patch({ office: office })
        this.triggerClientSave()
      }
      this.$patch({ availableUpdates: null })
    },

    versionIgnore(version: string) {
      const office = this.getOffice
      if (office) {
        office.ignored_version = version
        office.updated = Date.now()
        this.$patch({ office: office })
        this.triggerClientSave()
      }
      this.$patch({ availableUpdates: null })
    },
  },
})
